"use client";

import clsx from "clsx";
import { useEffect, useRef, useState } from "react";

import {
  ChatSequence,
  usePrefersReducedMotion,
} from "@/components/redesign/ChatSequence";
import { GailMark } from "@/components/redesign/MarketingPhoneChatVisual";
import { MarketingVisualBackdrop } from "@/components/redesign/MarketingVisualBackdrop";

export type WebChatConversationCopy = {
  headerTitle: string;
  gailGreeting: string;
  customerOpener: string;
  gailEmpathy: string;
  gailFollowUp: string;
  customerChoice: string;
  gailResolution: string;
};

/** Pause between the widget scrolling into view and the window opening. */
const OPEN_DELAY_MS = 250;

const CHAT_INCOMING =
  "rounded-xl rounded-tl-sm bg-[#f2f2f4] px-2.5 py-1.5 text-left text-[0.75rem] leading-snug text-black/85 md:px-3 md:py-2 md:text-[0.8125rem]";
const CHAT_OUTGOING =
  "max-w-[85%] rounded-xl rounded-tr-sm bg-gail-primary px-2.5 py-1.5 text-left text-[0.75rem] leading-snug text-white md:px-3 md:py-2 md:text-[0.8125rem]";
/** Avatar width + gap, so follow-up bubbles line up under the first one. */
const AVATAR_INDENT = "ml-[1.875rem] md:ml-[2.375rem]";

function CommentIcon({ className }: Readonly<{ className?: string }>) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <rect x="2.5" y="4" width="19" height="13.5" rx="3" />
      <path d="M7 17.5v3.4l4.4-3.4" />
      <path d="M7.5 9h9" />
      <path d="M7.5 12.5h5.5" />
    </svg>
  );
}

function ExpandIcon({ className }: Readonly<{ className?: string }>) {
  return (
    <svg
      viewBox="0 0 14 14"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <path d="M4.5 1.25H1.25v11.5H4.5" />
      <path d="M9.5 1.25h3.25v11.5H9.5" />
    </svg>
  );
}

function CloseIcon({ className }: Readonly<{ className?: string }>) {
  return (
    <svg
      viewBox="0 0 12 12"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      className={className}
    >
      <path d="m1.5 1.5 9 9M10.5 1.5l-9 9" />
    </svg>
  );
}

function GailAvatar() {
  return (
    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#dcebfd] text-gail-primary md:h-7 md:w-7">
      <GailMark className="h-3 w-auto md:h-3.5" />
    </span>
  );
}

/**
 * A live-chat widget that opens from its launcher bubble once it scrolls into
 * view, then plays the conversation one message at a time. The window stays
 * mounted and only toggles classes, so playback never mutates the DOM shape.
 */
export function WebChatVisual({
  conversation,
}: Readonly<{ conversation: WebChatConversationCopy }>) {
  const [opened, setOpened] = useState(false);
  const [inView, setInView] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const reducedMotion = usePrefersReducedMotion();
  const open = reducedMotion || opened;

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.35 },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!inView || opened) return;
    const timeout = setTimeout(() => setOpened(true), OPEN_DELAY_MS);
    return () => clearTimeout(timeout);
  }, [inView, opened]);

  return (
    <div
      ref={containerRef}
      className="flex h-full w-full flex-col items-stretch justify-center"
    >
      <div
        className={clsx(
          "flex min-h-0 origin-bottom-right flex-col overflow-hidden rounded-2xl bg-white",
          "shadow-[0_24px_48px_-24px_rgba(15,40,80,0.45)]",
          "transition-[transform,opacity] duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]",
          open ? "scale-100 opacity-100" : "scale-[0.15] opacity-0",
        )}
      >
        <div className="flex shrink-0 items-center gap-2 bg-gail-primary px-3 py-2 text-white md:gap-2.5 md:px-3.5 md:py-2.5">
          <CommentIcon className="h-4 w-auto md:h-5" />
          <span className="flex-1 truncate text-[0.8125rem] font-semibold tracking-tight md:text-[0.875rem]">
            {conversation.headerTitle}
          </span>
          <span className="flex h-5.5 w-5.5 items-center justify-center rounded-md bg-white/15 md:h-6.5 md:w-6.5">
            <ExpandIcon className="h-2.5 w-auto md:h-3" />
          </span>
          <span className="flex h-5.5 w-5.5 items-center justify-center rounded-md bg-white/15 md:h-6.5 md:w-6.5">
            <CloseIcon className="h-2 w-auto md:h-2.5" />
          </span>
        </div>

        <ChatSequence
          className="min-h-0 flex-1 space-y-2 overflow-hidden p-3 md:space-y-2.5 md:p-3.5"
          // The widget window crops the thread on mobile, so only part of it
          // can ever intersect.
          threshold={0.15}
          steps={[
            {
              align: "start",
              bubble: (
                <span className="flex max-w-[88%] items-start gap-1.5">
                  <GailAvatar />
                  <p className={CHAT_INCOMING}>{conversation.gailGreeting}</p>
                </span>
              ),
            },
            {
              align: "end",
              bubble: (
                <p className={CHAT_OUTGOING}>{conversation.customerOpener}</p>
              ),
            },
            {
              align: "start",
              typing: true,
              typingClassName: clsx(
                AVATAR_INDENT,
                "rounded-xl rounded-tl-sm bg-[#f2f2f4] text-black/70",
              ),
              bubble: (
                <span className="flex max-w-[88%] items-start gap-1.5">
                  <GailAvatar />
                  <p className={CHAT_INCOMING}>{conversation.gailEmpathy}</p>
                </span>
              ),
            },
            {
              align: "start",
              typing: true,
              typingClassName: clsx(
                AVATAR_INDENT,
                "rounded-xl rounded-tl-sm bg-[#f2f2f4] text-black/70",
              ),
              bubble: (
                <p className={clsx(CHAT_INCOMING, AVATAR_INDENT, "max-w-[80%]")}>
                  {conversation.gailFollowUp}
                </p>
              ),
            },
            {
              align: "end",
              bubble: (
                <p className={CHAT_OUTGOING}>{conversation.customerChoice}</p>
              ),
            },
            {
              align: "start",
              typing: true,
              typingClassName: clsx(
                AVATAR_INDENT,
                "rounded-xl rounded-tl-sm bg-[#f2f2f4] text-black/70",
              ),
              bubble: (
                <span className="flex max-w-[88%] items-start gap-1.5">
                  <GailAvatar />
                  <p className={CHAT_INCOMING}>{conversation.gailResolution}</p>
                </span>
              ),
            },
          ]}
        />
      </div>

      <div className="mt-2.5 flex shrink-0 justify-end md:mt-3">
        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-gail-primary text-white shadow-[0_12px_24px_-10px_rgba(0,98,226,0.6)] md:h-11 md:w-11">
          <GailMark className="h-4 w-auto md:h-5" />
        </span>
      </div>
    </div>
  );
}

/**
 * Replicates the layout of `MarketingFeatureImageVisual` with `fit="contain"`,
 * but hosts the live chat widget instead of an image.
 */
export function MarketingFeatureWebChatVisual({
  label,
  conversation,
  className,
}: Readonly<{
  /** Accessible description of the conversation (replaces the old image alt). */
  label: string;
  conversation: WebChatConversationCopy;
  className?: string;
}>) {
  return (
    <div
      className={clsx(
        "relative aspect-4/3 w-full overflow-hidden rounded-2xl md:aspect-square md:rounded-3xl",
        className,
      )}
    >
      <MarketingVisualBackdrop />
      <span className="sr-only">{label}</span>
      <div
        aria-hidden
        className="absolute inset-0 flex items-center justify-center"
      >
        <div className="flex h-full w-[78%] max-w-[340px] flex-col justify-center py-5 sm:w-[64%] md:py-8">
          <WebChatVisual conversation={conversation} />
        </div>
      </div>
    </div>
  );
}
